import React from 'react';

export function RiEmpty({ message = 'No data', height = 120 }) {
  return (
    <div
      className="flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 italic rounded-lg border border-dashed border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30 px-6"
      style={{ minHeight: height }}
    >
      {message}
    </div>
  );
}

/**
 * The "couldn't load" state: the request behind a section failed (timeout,
 * permission, network), so it must not read as "No data". Offers a retry when the
 * host can re-run the request.
 * @param {{ message?: string, onRetry?: () => void, height?: number }} props
 */
export function RiError({ message = "Couldn't load this section. The request failed or took too long.", onRetry, height = 120 }) {
  return (
    <div
      data-testid="ri-error"
      role="alert"
      className="flex flex-col items-center justify-center gap-2 text-center text-sm text-amber-800 dark:text-amber-200 rounded-lg border border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 px-6 py-4"
      style={{ minHeight: height }}
    >
      <span>{message}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="px-3 py-1 rounded-md border border-amber-300 dark:border-amber-700 text-xs font-medium text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function RiLoading({ height = 120 }) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse"
      style={{ minHeight: height }}
      role="status"
      aria-label="Loading"
    >
      <div className="space-y-2.5">
        {['w-1/3', 'w-2/3', 'w-1/2', 'w-3/4'].map((w, i) => (
          <div key={i} className={`h-3 rounded bg-gray-200 dark:bg-gray-800 ${w}`} />
        ))}
      </div>
    </div>
  );
}

function buildPageList(current, total, sibling = 1) {
  if (total <= 1) return [1];
  const left = Math.max(2, current - sibling);
  const right = Math.min(total - 1, current + sibling);
  const out = [1];
  if (left > 2) out.push('…');
  for (let i = left; i <= right; i += 1) {
    if (i !== 1 && i !== total) out.push(i);
  }
  if (right < total - 1) out.push('…');
  if (total > 1) out.push(total);
  return out;
}

export function RiPagination({ page, totalPages, onPageChange, sibling = 1, totalItems, itemLabel = 'row' }) {
  if (!totalPages || totalPages <= 1) return null;
  const items = buildPageList(page, totalPages, sibling);
  const baseBtn = 'px-2 py-1 rounded border text-xs disabled:opacity-40';
  const idle = 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800';
  const active = 'border-[#32B1FF] bg-[#32B1FF] text-white';
  const showCount = typeof totalItems === 'number';
  const nav = (
    <nav aria-label="Pagination" className="flex gap-1.5 text-xs flex-wrap max-[499px]:w-full max-[499px]:justify-between max-[499px]:flex-nowrap justify-end">
      <button type="button" disabled={page <= 1}
        onClick={() => onPageChange(Math.max(1, page - 1))}
        className={`${baseBtn} ${idle} max-[499px]:px-3`}>← Prev</button>
      {items.map((it, idx) => (
        it === '…'
          ? <span key={`e-${idx}`} className="px-2 py-1 text-gray-500 dark:text-gray-400 max-[499px]:hidden" aria-hidden="true">…</span>
          : (
            <button
              key={it}
              type="button"
              aria-current={it === page ? 'page' : undefined}
              onClick={() => onPageChange(it)}
              className={`${baseBtn} ${it === page ? active : idle} max-[499px]:hidden`}
            >{it}</button>
          )
      ))}
      <button type="button" disabled={page >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        className={`${baseBtn} ${idle} max-[499px]:px-3`}>Next →</button>
    </nav>
  );
  if (!showCount) return <div className="mt-3">{nav}</div>;
  return (
    <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-3">
      <span>{totalItems} {itemLabel}{totalItems === 1 ? '' : 's'}</span>
      {nav}
    </div>
  );
}

export function RiSparseNotice({ count, threshold = 4 }) {
  if (count == null || count <= 0 || count >= threshold) return null;
  return (
    <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 italic">
      Not enough data yet to plot a trend — check back next week.
    </div>
  );
}

export function InsightCard({ title, children, tone = 'info' }) {
  const toneClass = tone === 'warn'
    ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-900 dark:text-amber-200'
    : tone === 'good'
    ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-900 dark:text-emerald-200'
    : 'bg-[#32B1FF]/10 dark:bg-[#32B1FF]/10 border-[#32B1FF]/30 text-gray-900 dark:text-gray-100';
  return (
    <div className={`rounded-lg border p-3 text-sm ${toneClass}`}>
      {title && <div className="text-xs font-semibold uppercase tracking-wide mb-1">{title}</div>}
      {children}
    </div>
  );
}
